import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { debounce, uniqBy } from "lodash";
import { Col, Form, Row, Table } from "react-bootstrap";

import { get } from "utils/DeApi";
import SortColumn from "components/ui/SortColumn";
import MetaPagination from "components/ui/MetaPagination";
import { OrganizationContext } from "contexts/OrganizationProvider";

import MemberCreate from "./MemberCreate";
import MemberExport from "./MemberExport";
import MemberListItem from "./MemberListItem";

import "./Members.scss";

function Members() {
  const organization = useContext(OrganizationContext);
  const subscribedPromises = useRef([]);

  const [error, setError] = useState();
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState();

  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState("");
  const [perPage, setPerPage] = useState(25);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [tableSortState, setTableSortState] = useState({
    sortColumn: "updated_at",
    sortAsc: false,
  });

  const sendSearchQuery = useRef(
    debounce((q) => {
      setPage(1);
      setDebouncedQuery(q);
    }, 1000),
  );

  const sortMembers = (sortingvalue) => {
    setError(null);
    setIsLoading(true);
    setTableSortState({
      sortAsc: !tableSortState.sortAsc,
      sortColumn: sortingvalue,
    });
  };

  const fetchMembers = useCallback(
    ({ silentLoader = false }) => {
      setError(null);
      if (!silentLoader) setIsLoading(true);

      const { sortAsc, sortColumn } = tableSortState;

      const membersPromise = get(`organizations/${organization?.id}/members`, {
        params: {
          page: page ? page : 1,
          perPage: perPage || 10,
          sort: !!sortAsc ? sortColumn : `-${sortColumn}`,
          "filter[search]": debouncedQuery ? debouncedQuery : "",
        },
      });
      membersPromise.promise
        .then(({ data, meta }) => {
          setMeta(meta);
          setMembers(uniqBy(data, "subscriberId"));
        })
        .catch((error) => {
          !error.isCanceled && setError(error);
        })
        .finally(() => setIsLoading(false));

      subscribedPromises.current.push(membersPromise);
    },
    [tableSortState, organization?.id, page, perPage, debouncedQuery],
  );

  useEffect(() => {
    fetchMembers({ silentLoader: false });

    const promises = subscribedPromises.current;
    return () => {
      promises.forEach((promise) => {
        promise.cancel();
      });
    };
  }, [fetchMembers]);

  const refreshMembers = () => fetchMembers({ silentLoader: true });

  return (
    <Row>
      <Col xs={12} sm={12} md={12} lg={12} className="mb-4">
        <Row>
          <Form.Group as={Col} lg={4} md={12} sm={12} xs={12} className="mb-3">
            <Form.Label>
              <small>Search by name or email</small>
            </Form.Label>
            <Form.Control
              type="text"
              value={query}
              onChange={(e) => {
                const q = e.target.value;
                setQuery(q);
                sendSearchQuery.current(q);
              }}
              placeholder="Search"
            />
          </Form.Group>
          <Col className="text-end pt-lg-4 mb-3">
            <MemberExport
              handleError={setError}
              tableSortState={tableSortState}
              debouncedQuery={debouncedQuery}
            />
            <MemberCreate onMemberCreated={refreshMembers} />
          </Col>
        </Row>
        <Table hover responsive>
          <thead>
            <tr>
              <th
                className="w-15"
                onClick={() => !isLoading && sortMembers("first_name")}
              >
                First Name
                <SortColumn
                  tableSortState={tableSortState}
                  column={"first_name"}
                />
              </th>
              <th
                className="w-15"
                onClick={() => !isLoading && sortMembers("last_name")}
              >
                Last Name
                <SortColumn
                  tableSortState={tableSortState}
                  column={"last_name"}
                />
              </th>
              <th
                className="w-15"
                onClick={() => !isLoading && sortMembers("email")}
              >
                Email
                <SortColumn tableSortState={tableSortState} column={"email"} />
              </th>
              <th className="w-10">Role</th>
              <th className="w-7">Entities</th>
              <th
                className="w-15"
                onClick={() =>
                  !isLoading && sortMembers("subscriber_joined_at")
                }
              >
                Joined
                <SortColumn
                  tableSortState={tableSortState}
                  column={"subscriber_joined_at"}
                />
              </th>
              <th
                className="w-15"
                onClick={() => !isLoading && sortMembers("updated_at")}
              >
                Updated
                <SortColumn
                  tableSortState={tableSortState}
                  column={"updated_at"}
                />
              </th>
              <th className="w-15">Action</th>
            </tr>
          </thead>
          <tbody className="table-group-divider">
            <MemberListItem
              error={error}
              members={members}
              isLoading={isLoading}
              setMembers={setMembers}
              handleMemberUpdated={refreshMembers}
              handleMemberDeleted={refreshMembers}
            />
          </tbody>
        </Table>
        <MetaPagination
          meta={meta}
          perPage={perPage}
          setPage={setPage}
          setPerPage={setPerPage}
        />
      </Col>
    </Row>
  );
}

export default Members;
